import { Spinner } from '@design-systems/canvas-ui'
import { lazy, Suspense, useState, useRef, useEffect, useMemo } from 'react'
import {
  COMMENTS_MAX_LENGTH,
  MANUFACTURING_TYPE_ID_FINISHED_GOOD,
  MANUFACTURING_TYPE_ID_RAWMATERIAL,
  MANUFACTURING_TYPE_ID_SECONDARY_PROCESSING,
  MESSAGE_HEADER_SUBMIT_FAIL,
  STATUS_APPROVE_BUT_HOLD,
  STATUS_APPROVED,
  STATUS_COMPLETE_REVIEW,
  STATUS_REJECTED,
  STATUS_SENT_BACK,
} from '../../../../common/commonConstants'
import { getCapabilityPopupSpecification } from '../../../../globalComponents/RfiConstants'
import { useData } from '../context/DataContext'
import { useUi, TYPES as UT } from '../context/UiContext'

const RfiFormConfirmationModal = lazy(() =>
  import('../../../../globalComponents/RfiCommons/popup').then((m) => ({
    default: m.RfiFormConfirmationModal,
  })),
)
const RfiFormApprovalConfirmationModal = lazy(() =>
  import('../../../../globalComponents/RfiCommons/popup').then((m) => ({
    default: m.RfiFormApprovalConfirmationModal,
  })),
)
const EmailModal = lazy(() =>
  import('../../../../globalComponents/RfiCommons/popup').then((m) => ({
    default: m.EmailModal,
  })),
)
const RFIErrorModal = lazy(() =>
  import('../../../../globalComponents/RfiCommons/popup').then((m) => ({
    default: m.RFIErrorModal,
  })),
)
const CTVContactsModal = lazy(
  () =>
    import('../../../../globalComponents/RfiCommons/popup/CTVContactsModal'),
)
const CTPContactsModal = lazy(
  () =>
    import('../../../../globalComponents/RfiCommons/popup/CTPContactsModal'),
)
const RawMaterialCapabilitiesConfirmationModal = lazy(
  () =>
    import('../../../../globalComponents/RfiCommons/popup/RawMaterialCapabilitiesConfirmationModal'),
)
const SecondaryProcessingCapabilitiesConfirmationModal = lazy(
  () =>
    import('../../../../globalComponents/RfiCommons/popup/SecondaryProcessingCapabilitiesConfirmationModal'),
)
const RfiProductCapabilitiesConfimationModal = lazy(
  () =>
    import('../../../../globalComponents/RfiCommons/popup/RfiProductCapabilitiesConfimationModal'),
)
const RfiPrefillInformationModal = lazy(
  () =>
    import('../../../../globalComponents/RfiCommons/popup/RfiPrefillInformationModal'),
)

export default function RFIModals({
  rfiType,
  manufacturingTypeId,
  submitHooks,
  approvalHooks,
}) {
  const { state: d } = useData()
  const { state: u, dispatch: ud } = useUi()
  const [ctpInitialStep, setCtpInitialStep] = useState(null)
  const ctpModalRef = useRef(null)

  const [keepCtpMounted, setKeepCtpMounted] = useState(false)
  const [isCtpDataPresent, setIsCtpDataPresent] = useState(false)

  useEffect(() => {
    if (u.modals.ctpContacts) {
      setKeepCtpMounted(true)
    } else if (
      !u.userAction &&
      !u.modals.email &&
      !u.modals.actionConfirmation
    ) {
      setKeepCtpMounted(false)
    }
  }, [
    u.modals.ctpContacts,
    u.modals.email,
    u.modals.actionConfirmation,
    u.userAction,
  ])

  const handleSaveCtpData = () => {
    if (rfiType === 'location' && ctpModalRef.current?.handleSaveClick) {
      ctpModalRef.current.handleSaveClick(true)
    }
  }

  const handleEmailBackToPsqa = () => {
    ud({ type: UT.SET_MODAL, payload: { email: false } })
    setCtpInitialStep('PSQA')
    ud({ type: UT.SET_MODAL, payload: { ctpContacts: true } })
  }

  const closeConfirmation = () => {
    ud({ type: UT.SET_MODAL, payload: { confirmation: false } })
    ud({ type: UT.SET_FLAGS, payload: { isSaveAndCloseClicked: false } })
    ud({
      type: UT.SET_LOADING,
      payload: { submit: false, submitFailed: false },
    })
    ud({ type: UT.SET_SUBMIT_FAILED, payload: false })
  }

  const closeRFIProductCapabilitiesConfirmationModal = () => {
    ud({
      type: UT.SET_LOADING,
      payload: { action: false, actionFailed: false },
    })
    ud({
      type: UT.SET_MODAL,
      payload: { productCapabilities: { isVisible: false, isReadOnly: false } },
    })
    ud({ type: UT.SET_ACTION, payload: { userAction: '' } })
  }

  const isNonFG =
    Number(manufacturingTypeId) !== Number(MANUFACTURING_TYPE_ID_FINISHED_GOOD)

  const ctpFactoryAddress = useMemo(() => {
    const addressArr =
      d?.rfiObject?.dynamic_sections?.other_information
        ?.additional_factory_information?.factory_address?.address || []
    return addressArr
      .flatMap((element) => element?.values || [])
      .filter(Boolean)
      .join(', ')
  }, [d?.rfiObject])

  return (
    <Suspense fallback={<Spinner size="lg" />}>
      {rfiType === 'location' &&
        !u.loading.fetch &&
        !u.fetchFailed &&
        !u.isReadonly &&
        d?.rfiObject?.show_prefill_popup && (
          <RfiPrefillInformationModal
            isVisible={u.modals.prefill}
            onRefuse={approvalHooks.closeRFIPrefillModal}
            onConfirm={approvalHooks.closeRFIPrefillModal}
          />
        )}

      {u.modals.confirmation && (
        <RfiFormConfirmationModal
          isVisible={u.modals.confirmation}
          isSaveAndCloseClicked={u.isSaveAndCloseClicked}
          closeConfirmation={closeConfirmation}
          rfiFinalConfirmationActionClickHandler={
            submitHooks.rfiFinalConfirmationActionClickHandler
          }
          rfiSaveAndContinueActionClickHandler={
            submitHooks.rfiSaveAndContinueActionClickHandler
          }
        />
      )}

      {isNonFG &&
        u.modals.productCapabilities?.isVisible &&
        (Number(manufacturingTypeId) ===
        Number(MANUFACTURING_TYPE_ID_RAWMATERIAL) ? (
          <RawMaterialCapabilitiesConfirmationModal
            isRfiNextActionPCInProgress={u.loading.nextActionPC}
            isRfiNextActionPCFailed={u.nextActionPCFailed}
            closeRFIProductCapabilitiesConfirmationModal={
              closeRFIProductCapabilitiesConfirmationModal
            }
            commentsMaxLen={COMMENTS_MAX_LENGTH}
            settings={u.modals.productCapabilities}
            capabilitiesList={d?.rfiObject?.capability_vetting_status}
            existingComment={d?.rfiObject?.vetting_status_comments}
            userComments={u.userComments || ''}
            onNextActionConfirmation={(c, list, isCapabilityManagementMode) =>
              approvalHooks.onCapabilityNextActionConfirmation(
                c,
                list,
                isCapabilityManagementMode,
              )
            }
            manufacturingTypeId={manufacturingTypeId}
            rfiType={rfiType}
            specifications={getCapabilityPopupSpecification(
              rfiType,
              manufacturingTypeId,
            )}
            isCapabilityManagementMode={!u.userAction}
          />
        ) : Number(manufacturingTypeId) ===
          Number(MANUFACTURING_TYPE_ID_SECONDARY_PROCESSING) ? (
          <SecondaryProcessingCapabilitiesConfirmationModal
            isRfiNextActionPCInProgress={u.loading.nextActionPC}
            isRfiNextActionPCFailed={u.nextActionPCFailed}
            closeRFIProductCapabilitiesConfirmationModal={
              closeRFIProductCapabilitiesConfirmationModal
            }
            commentsMaxLen={COMMENTS_MAX_LENGTH}
            settings={u.modals.productCapabilities}
            capabilitiesList={d?.rfiObject?.capability_vetting_status}
            existingComment={d?.rfiObject?.vetting_status_comments}
            userComments={u.userComments || ''}
            onNextActionConfirmation={(c, list, isCapabilityManagementMode) =>
              approvalHooks.onCapabilityNextActionConfirmation(
                c,
                list,
                isCapabilityManagementMode,
              )
            }
            manufacturingTypeId={manufacturingTypeId}
            rfiType={rfiType}
            specifications={getCapabilityPopupSpecification(
              rfiType,
              manufacturingTypeId,
            )}
            isCapabilityManagementMode={!u.userAction}
          />
        ) : (
          <RfiProductCapabilitiesConfimationModal
            isRfiNextActionPCInProgress={u.loading.nextActionPC}
            isRfiNextActionPCFailed={u.nextActionPCFailed}
            closeRFIProductCapabilitiesConfirmationModal={
              closeRFIProductCapabilitiesConfirmationModal
            }
            commentsMaxLen={COMMENTS_MAX_LENGTH}
            settings={u.modals.productCapabilities}
            capabilitiesList={d?.rfiObject?.capability_vetting_status}
            existingComment={d?.rfiObject?.vetting_status_comments}
            userComments={u.userComments || ''}
            onNextActionConfirmation={(c, list, isCapabilityManagementMode) =>
              approvalHooks.onCapabilityNextActionConfirmation(
                c,
                list,
                isCapabilityManagementMode,
              )
            }
            manufacturingTypeId={manufacturingTypeId}
            rfiType={rfiType}
            specifications={getCapabilityPopupSpecification(
              rfiType,
              manufacturingTypeId,
            )}
            isCapabilityManagementMode={!u.userAction}
          />
        ))}

      {u.modals.actionConfirmation && (
        <RfiFormApprovalConfirmationModal
          isVisible={u.modals.actionConfirmation}
          rfiType={rfiType}
          userAction={u.userAction}
          closeRFIActionConfirmationModal={
            approvalHooks.closeRFIActionConfirmationModal
          }
          userComments={u.userComments}
          setUserCommentsValue={(e) =>
            approvalHooks.setUserCommentsValue(e, COMMENTS_MAX_LENGTH)
          }
          commentsMaxLen={COMMENTS_MAX_LENGTH}
          onActionConfirmation={approvalHooks.onActionConfirmation}
          manufacturingTypeId={manufacturingTypeId}
          onSaveCtpData={
            rfiType === 'location' && keepCtpMounted && !isCtpDataPresent
              ? handleSaveCtpData
              : undefined
          }
        />
      )}

      {u.modals.submitValidation && (
        <RFIErrorModal
          isVisible={u.modals.submitValidation}
          heading={MESSAGE_HEADER_SUBMIT_FAIL}
          closeErrorDialog={() =>
            ud({ type: UT.SET_MODAL, payload: { submitValidation: false } })
          }
        />
      )}

      <EmailModal
        isVisible={u.modals.email}
        closeEmailDialog={
          [
            STATUS_APPROVED,
            STATUS_REJECTED,
            STATUS_SENT_BACK,
            STATUS_COMPLETE_REVIEW,
            STATUS_APPROVE_BUT_HOLD,
          ]
            .map((s) => String(s || '').toLowerCase())
            .includes(String(u.userAction || '').toLowerCase())
            ? approvalHooks.closeEmailDialogForApprovalActions
            : submitHooks.closeEmailDialog
        }
        onBack={
          rfiType === 'location' && keepCtpMounted && !isCtpDataPresent
            ? handleEmailBackToPsqa
            : undefined
        }
        supplierId={d?.rfiObject?.supplier_id}
        locationId={d?.rfiObject?.location_id}
        manufacturingTypeId={manufacturingTypeId}
        rfiType={rfiType}
      />

      {u.modals.ctvContacts && (
        <CTVContactsModal
          isVisible={u.modals.ctvContacts}
          closeCTVContactsDialog={(isLaunch, isSaveClose) =>
            approvalHooks.closeCTVContactsDialog(isLaunch, isSaveClose)
          }
          supplierId={d?.rfiObject?.supplier_id}
          supplierName={d?.rfiObject?.supplier_name}
          manufacturingTypeId={manufacturingTypeId}
        />
      )}
      {keepCtpMounted && (
        <CTPContactsModal
          ref={ctpModalRef}
          selectedLocationId={d?.rfiObject?.location_id}
          supplierId={d?.rfiObject?.supplier_id}
          supplierName={d?.rfiObject?.supplier_name}
          locationName={d?.rfiObject?.location_name}
          address={ctpFactoryAddress}
          manufacturingTypeId={manufacturingTypeId}
          isVisible={u.modals.ctpContacts}
          initialStep={ctpInitialStep}
          onCtpDataPresent={setIsCtpDataPresent}
          setIsModalVisible={(visible) =>
            ud({ type: UT.SET_MODAL, payload: { ctpContacts: visible } })
          }
          closeCTPContactsDialog={(isLaunch, isSaveClose) => {
            setCtpInitialStep(null)
            if (!isLaunch) {
              setKeepCtpMounted(false)
            }
            approvalHooks.closeCTPContactsDialog(isLaunch, isSaveClose)
          }}
        />
      )}
    </Suspense>
  )
}
